import React, { useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Input, Button, message, Spin, Empty, Pagination, Dropdown } from 'antd';
import { SearchOutlined, ShoppingCartOutlined, DownOutlined, UpOutlined, CheckCircleFilled, ExclamationCircleFilled } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { productAPI, categoryAPI, collectionAPI, dropshipperAPI } from '../api';
import { BRAND_PRIMARY } from '../utils/brandColors';

const { Search } = Input;
const { Meta } = Card;

// 获取API基础URL
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api/v1';

const ProductsList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState('');
  const navigate = useNavigate();
  const [messageApi, contextHolder] = message.useMessage();
  
  // 分页相关状态
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 30,
    total: 0,
  });

  // 分类相关状态
  const [categories, setCategories] = useState([]);
  const [multiLevelCategories, setMultiLevelCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [hoveredTopCategory, setHoveredTopCategory] = useState(null);

  // 商品集相关状态
  const [collections, setCollections] = useState([]);
  const [multiLevelCollections, setMultiLevelCollections] = useState({});
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedCollectionIds, setSelectedCollectionIds] = useState([]);
  const [collectionDropdownVisible, setCollectionDropdownVisible] = useState(false);
  const [hoveredTopCollection, setHoveredTopCollection] = useState(null);
  const [addingProductId, setAddingProductId] = useState(null);
  const [sorterState, setSorterState] = useState({ field: 'created_at', order: 'descend' });

  const fetchProducts = useCallback(async (
    page = 1,
    categoryIds = [],
    collectionIds = [],
    keyword = '',
    sortField = sorterState.field,
    sortOrder = sorterState.order
  ) => {
    try {
      setLoading(true);
      const params = { page, page_size: pagination.pageSize };

      if (categoryIds && categoryIds.length > 0) {
        params.categories = categoryIds;
      }
      if (collectionIds && collectionIds.length > 0) {
        params.collections = collectionIds;
      }
      
      const trimmedKeyword = keyword && keyword.trim();
      if (trimmedKeyword) {
        params.search = trimmedKeyword;
      }

      if (sortField && sortOrder) {
        params.sort_by = sortField;
        params.sort_order = sortOrder === 'ascend' ? 'asc' : 'desc';
      }
      
      const response = await productAPI.getPublic(params);
      
      setProducts(response.data.data || []);
      setPagination(prev => ({
        ...prev,
        current: response.data.page,
        pageSize: response.data.page_size || prev.pageSize,
        total: response.data.total,
      }));
    } catch (error) {
      
      messageApi.error('Failed to load products');
    } finally {
      setLoading(false);
    }
  }, [pagination.pageSize, sorterState.field, sorterState.order, messageApi]);

  // 获取分类数据
  const fetchCategories = async () => {
    try {
      const response = await categoryAPI.getAll();
      setCategories(response.data);
    } catch (error) {
      
    }
  };

  // 获取多级分类数据
  const fetchMultiLevelCategories = async () => {
    try {
      const response = await categoryAPI.getMultiLevel();
      setMultiLevelCategories(response.data.data || {});
    } catch (error) {
      
    }
  };

  // 获取商品集数据
  const fetchCollections = async () => {
    try {
      const response = await collectionAPI.getAll();
      setCollections(response.data);
    } catch (error) {
      
    }
  };

  // 获取多级商品集数据
  const fetchMultiLevelCollections = async () => {
    try {
      const response = await collectionAPI.getMultiLevel();
      setMultiLevelCollections(response.data.data || {});
    } catch (error) {
      
    }
  };

  useEffect(() => {
    fetchProducts();
    fetchCategories();
    fetchMultiLevelCategories();
    fetchCollections();
    fetchMultiLevelCollections();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // 分类相关辅助函数
  const getAllCategoryIds = (data) => {
    let ids = [];
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'number') {
        ids.push(value);
      } else if (typeof value === 'object') {
        ids = ids.concat(getAllCategoryIds(value));
      }
    });
    return ids;
  };

  // 获取某个节点下所有最终分类ID
  const getChildCategoryIds = (data) => {
    if (typeof data === 'number') {
      return [data];
    }
    return getAllCategoryIds(data);
  };

  // 获取一级分类列表
  const getTopLevelCategories = () => {
    return Object.keys(multiLevelCategories);
  };

  // 渲染子分类树（右侧面板）
  const renderSubCategoryTree = (data, level = 0) => {
    if (!data) return null;
    
    return Object.entries(data).map(([key, value]) => {
      const indentStyle = {
        paddingLeft: `${level * 20 + 16}px`,
        padding: `8px 16px 8px ${level * 20 + 16}px`,
        fontSize: '14px',
        cursor: 'pointer',
        borderBottom: '1px solid #f5f5f5',
        transition: 'background-color 0.2s'
      };

      if (typeof value === 'number') {
        // 叶子节点（最终商品分类）
        const category = categories.find(cat => cat.id === value);
        const categoryName = category ? category.name : `ID: ${value}`;
        
        return (
          <div
            key={`leaf-${value}`}
            style={{
              ...indentStyle,
              color: '#333'
            }}
            onClick={() => handleCategorySelect(categoryName, [value])}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f2f5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            {categoryName}
          </div>
        );
      } else {
        // 非叶子节点
        const childIds = getChildCategoryIds(value);
        return (
          <div key={key}>
            <div
              style={{
                ...indentStyle,
                fontWeight: level === 0 ? '600' : '500',
                color: level === 0 ? BRAND_PRIMARY : '#666',
                backgroundColor: level === 0 ? '#e5f3ea' : 'transparent'
              }}
              onClick={() => handleCategorySelect(key, childIds)}
              onMouseEnter={(e) => {
                if (level > 0) e.target.style.backgroundColor = '#edf5f0';
              }}
              onMouseLeave={(e) => {
                if (level > 0) e.target.style.backgroundColor = 'transparent';
              }}
            >
              {key}
            </div>
            {renderSubCategoryTree(value, level + 1)}
          </div>
        );
      }
    });
  };

  // 构建大面板内容
  const buildCategoryPanel = () => {
    const topCategories = getTopLevelCategories();
    const currentSubData = hoveredTopCategory ? multiLevelCategories[hoveredTopCategory] : null;

    return (
      <div style={{
        display: 'flex',
        width: '800px',
        height: '400px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        overflow: 'hidden'
      }}>
        {/* 左侧一级分类列表 */}
        <div style={{
          width: '250px',
          backgroundColor: '#fafafa',
          borderRight: '1px solid #e8e8e8',
          overflowY: 'auto'
        }}>
          {/* All Categories 选项 */}
          <div
            style={{
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#666',
              cursor: 'pointer',
              borderBottom: '1px solid #e8e8e8',
              backgroundColor: !hoveredTopCategory ? '#e5f3ea' : 'transparent'
            }}
            onMouseEnter={() => setHoveredTopCategory(null)}
            onClick={() => handleResetCategory()}
          >
            All Products
          </div>
          
          {topCategories.map(topCat => (
            <div
              key={topCat}
              style={{
                padding: '12px 16px',
                fontSize: '14px',
                cursor: 'pointer',
                borderBottom: '1px solid #e8e8e8',
                backgroundColor: hoveredTopCategory === topCat ? '#e5f3ea' : 'transparent',
                color: hoveredTopCategory === topCat ? BRAND_PRIMARY : '#333',
                fontWeight: hoveredTopCategory === topCat ? '500' : 'normal',
                transition: 'all 0.2s'
              }}
              onMouseEnter={() => setHoveredTopCategory(topCat)}
              onClick={() => {
                const childIds = getChildCategoryIds(multiLevelCategories[topCat]);
                handleCategorySelect(topCat, childIds);
              }}
            >
              {topCat}
              <span style={{ 
                float: 'right', 
                fontSize: '12px', 
                color: '#999',
                transform: hoveredTopCategory === topCat ? 'translateX(-2px)' : 'translateX(0)',
                transition: 'transform 0.2s'
              }}>
                →
              </span>
            </div>
          ))}
        </div>

        {/* 右侧子分类面板 */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: 'white'
        }}>
          {hoveredTopCategory && currentSubData ? (
            <div>
              {renderSubCategoryTree(currentSubData)}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#999',
              fontSize: '14px'
            }}>
              {hoveredTopCategory ? 'Loading subcategories...' : 'Select a category to view subcategories'}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 处理分类选择
  const handleCategorySelect = (categoryName, categoryIds) => {
    setSelectedCategory(categoryName);
    setSelectedCategoryIds(categoryIds);
    setDropdownVisible(false);
    setHoveredTopCategory(null); // 重置悬浮状态
    // 重置商品集选择
    setSelectedCollection(null);
    setSelectedCollectionIds([]);
    // 重新获取第一页数据，保持搜索关键词
    fetchProducts(1, categoryIds, [], searchKeyword);
  };

  // 重置分类选择
  const handleResetCategory = () => {
    setSelectedCategory(null);
    setSelectedCategoryIds([]);
    setDropdownVisible(false);
    setHoveredTopCategory(null); // 重置悬浮状态
    // 如果有商品集选择，保持商品集，保持搜索关键词
    fetchProducts(1, [], selectedCollectionIds, searchKeyword);
  };

  // 商品集相关辅助函数
  const getAllCollectionIds = (data) => {
    let ids = [];
    Object.entries(data).forEach(([key, value]) => {
      if (typeof value === 'number') {
        ids.push(value);
      } else if (typeof value === 'object') {
        ids = ids.concat(getAllCollectionIds(value));
      }
    });
    return ids;
  };

  // 获取某个节点下所有最终商品集ID
  const getChildCollectionIds = (data) => {
    if (typeof data === 'number') {
      return [data];
    }
    return getAllCollectionIds(data);
  };

  // 获取一级商品集列表
  const getTopLevelCollections = () => {
    return Object.keys(multiLevelCollections);
  };

  // 渲染子商品集树（右侧面板）
  const renderSubCollectionTree = (data, level = 0) => {
    if (!data) return null;
    
    return Object.entries(data).map(([key, value]) => {
      const indentStyle = {
        paddingLeft: `${level * 20 + 16}px`,
        padding: `8px 16px 8px ${level * 20 + 16}px`,
        fontSize: '14px',
        cursor: 'pointer',
        borderBottom: '1px solid #f5f5f5',
        transition: 'background-color 0.2s'
      };

      if (typeof value === 'number') {
        // 叶子节点（最终商品集）
        const collection = collections.find(col => col.id === value);
        const collectionName = collection ? collection.name : `ID: ${value}`;
        
        return (
          <div
            key={`leaf-${value}`}
            style={{
              ...indentStyle,
              color: '#333'
            }}
            onClick={() => handleCollectionSelect(collectionName, [value])}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#f0f2f5';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = 'transparent';
            }}
          >
            {collectionName}
          </div>
        );
      } else {
        // 非叶子节点
        const childIds = getChildCollectionIds(value);
        return (
          <div key={key}>
            <div
              style={{
                ...indentStyle,
                fontWeight: level === 0 ? '600' : '500',
                color: level === 0 ? BRAND_PRIMARY : '#666',
                backgroundColor: level === 0 ? '#f4f7ff' : 'transparent'
              }}
              onClick={() => handleCollectionSelect(key, childIds)}
              onMouseEnter={(e) => {
                if (level > 0) e.target.style.backgroundColor = '#f0f2f5';
              }}
              onMouseLeave={(e) => {
                if (level > 0) e.target.style.backgroundColor = 'transparent';
              }}
            >
              {key}
            </div>
            {renderSubCollectionTree(value, level + 1)}
          </div>
        );
      }
    });
  };

  // 构建商品集面板
  const buildCollectionPanel = () => {
    const topCollections = getTopLevelCollections();
    const currentSubData = hoveredTopCollection ? multiLevelCollections[hoveredTopCollection] : null;

    return (
      <div style={{
        display: 'flex',
        width: '800px',
        height: '400px',
        backgroundColor: 'white',
        borderRadius: '8px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        overflow: 'hidden'
      }}>
        {/* 左侧一级商品集列表 */}
        <div style={{
          width: '250px',
          backgroundColor: '#fafafa',
          borderRight: '1px solid #e8e8e8',
          overflowY: 'auto'
        }}>
          {/* All Collections 选项 */}
          <div
            style={{
              padding: '12px 16px',
              fontSize: '14px',
              fontWeight: '600',
              color: '#666',
              cursor: 'pointer',
              borderBottom: '1px solid #e8e8e8',
              backgroundColor: !hoveredTopCollection ? '#ffe7d9' : 'transparent'
            }}
            onMouseEnter={() => setHoveredTopCollection(null)}
            onClick={() => handleResetCollection()}
          >
            All Products
          </div>
          
          {topCollections.map(topCol => (
            <div
              key={topCol}
              style={{
                padding: '12px 16px',
                fontSize: '14px',
                cursor: 'pointer',
                borderBottom: '1px solid #e8e8e8',
                backgroundColor: hoveredTopCollection === topCol ? '#e8f0ff' : 'transparent',
                color: hoveredTopCollection === topCol ? BRAND_PRIMARY : '#333',
                fontWeight: hoveredTopCollection === topCol ? '500' : 'normal',
                transition: 'all 0.2s'
              }}
              onMouseEnter={() => setHoveredTopCollection(topCol)}
              onClick={() => {
                const childIds = getChildCollectionIds(multiLevelCollections[topCol]);
                handleCollectionSelect(topCol, childIds);
              }}
            >
              {topCol}
              <span style={{ 
                float: 'right', 
                fontSize: '12px', 
                color: '#999',
                transform: hoveredTopCollection === topCol ? 'translateX(-2px)' : 'translateX(0)',
                transition: 'transform 0.2s'
              }}>
                →
              </span>
            </div>
          ))}
        </div>

        {/* 右侧子商品集面板 */}
        <div style={{
          flex: 1,
          overflowY: 'auto',
          backgroundColor: 'white'
        }}>
          {hoveredTopCollection && currentSubData ? (
            <div>
              {renderSubCollectionTree(currentSubData)}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              color: '#999',
              fontSize: '14px'
            }}>
              {hoveredTopCollection ? 'Loading subcollections...' : 'Select a collection to view subcollections'}
            </div>
          )}
        </div>
      </div>
    );
  };

  // 处理商品集选择
  const handleCollectionSelect = (collectionName, collectionIds) => {
    setSelectedCollection(collectionName);
    setSelectedCollectionIds(collectionIds);
    setCollectionDropdownVisible(false);
    setHoveredTopCollection(null);
    // 重置分类选择
    setSelectedCategory(null);
    setSelectedCategoryIds([]);
    // 重新获取第一页数据，保持搜索关键词
    fetchProducts(1, [], collectionIds, searchKeyword);
  };

  // 重置商品集选择
  const handleResetCollection = () => {
    setSelectedCollection(null);
    setSelectedCollectionIds([]);
    setCollectionDropdownVisible(false);
    setHoveredTopCollection(null);
    fetchProducts(1, selectedCategoryIds, [], searchKeyword);
  };

  const handleAddToStore = async (product) => {
    const token = localStorage.getItem('token');
    if (!token) {
      messageApi.info('Please login to add product to My Products');
      navigate('/login');
      return;
    }

    try {
      setAddingProductId(product.id);
      await dropshipperAPI.addProduct({
        product_id: product.id,
      });

      messageApi.success({
        icon: <CheckCircleFilled style={{ color: '#52c41a' }} />,
        content: (
          <span>
            Product has been added to{' '}
            <a
              href="/dropshipper/my-products"
              onClick={(e) => {
                e.preventDefault();
                navigate('/dropshipper/my-products');
              }}
              style={{ fontWeight: 600 }}
            >
              My Products
            </a>
          </span>
        ),
        duration: 3,
      });
    } catch (error) {
      const detail = error.response?.data?.detail || 'Failed to add product';
      if (detail === 'This product is already in My Products') {
        messageApi.open({
          type: 'warning',
          icon: <ExclamationCircleFilled style={{ color: '#faad14' }} />,
          content: (
            <span>
              This product is already in{' '}
              <a
                href="/dropshipper/my-products"
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/dropshipper/my-products');
                }}
                style={{ fontWeight: 600 }}
              >
                My Products
              </a>
            </span>
          ),
          duration: 3,
        });
      } else {
        messageApi.error(detail);
      }
    } finally {
      setAddingProductId(null);
    }
  };

  // 处理分页变化
  const handlePageChange = (page) => {
    fetchProducts(page, selectedCategoryIds, selectedCollectionIds, searchKeyword);
  };

  const handleSearch = (value) => {
    setSearchKeyword(value);
    // 调用后端搜索API，重置到第一页
    fetchProducts(1, selectedCategoryIds, selectedCollectionIds, value);
  };

  const applySort = (field, order) => {
    setSorterState({ field, order });
    setPagination(prev => ({ ...prev, current: 1 }));
    fetchProducts(1, selectedCategoryIds, selectedCollectionIds, searchKeyword, field, order);
  };

  const handleSortByNew = () => applySort('created_at', 'descend');

  const handleSortByPopular = () => applySort('purchase_count', 'descend');

  const handleSortByPrice = () => {
    const nextOrder = sorterState.field === 'price' && sorterState.order === 'descend'
      ? 'ascend'
      : 'descend';
    applySort('price', nextOrder);
  };

  const getSortButtonStyle = (field) => ({
    color: sorterState.field === field ? BRAND_PRIMARY : '#666',
    fontWeight: sorterState.field === field ? 600 : 400,
    padding: '0 6px',
  });

  const getPriceArrowColor = (direction) => {
    if (sorterState.field !== 'price') return '#999';
    if (direction === 'up') {
      return sorterState.order === 'ascend' ? BRAND_PRIMARY : '#999';
    }
    return sorterState.order === 'descend' ? BRAND_PRIMARY : '#999';
  };

  const getProductImage = (product) => {
    if (product.cover_picture && product.images) {
      const coverImage = product.images.find(img => img.id === product.cover_picture);
      if (coverImage) {
        return `${API_BASE_URL}/products/${product.id}/images/${coverImage.id}`;
      }
    }
    
    // 如果没有封面图片但有其他图片，使用第一张
    if (product.images && product.images.length > 0) {
      return `${API_BASE_URL}/products/${product.id}/images/${product.images[0].id}`;
    }
    
    // 默认占位图片
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
  };

  // 获取产品价格的辅助函数
  const getProductPrice = (product) => {
    if (!product.variants) {
      return { price: 0, currency: 'USD' };
    }

    // 如果是单品（直接包含SKU）
    if (product.variants.SKU !== undefined) {
      return {
        price: product.variants.price || 0,
        currency: product.variants.currency || 'USD'
      };
    }

    // 如果是多变体，找到所有变体的价格并返回最低价
    const allVariantPrices = [];
    
    const collectAllVariantPrices = (obj) => {
      if (obj && typeof obj === 'object') {
        if (obj.SKU !== undefined) {
          allVariantPrices.push({
            price: obj.price || 0,
            currency: obj.currency || 'USD'
          });
        } else {
          for (const value of Object.values(obj)) {
            collectAllVariantPrices(value);
          }
        }
      }
    };

    collectAllVariantPrices(product.variants);

    if (allVariantPrices.length === 0) {
      return { price: 0, currency: 'USD' };
    }

    // 找到最低价格（假设所有变体都是同一货币）
    const lowestPrice = Math.min(...allVariantPrices.map(v => v.price));
    const currency = allVariantPrices[0].currency || 'USD';

    return { price: lowestPrice, currency };
  };

  return (
    <>
      {contextHolder}
      <div>
      {/* Page Title and Search */}
      <div style={{
        marginBottom: '32px'
      }}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          marginBottom: '24px'
        }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '8px' }}>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              color: '#333',
                marginBottom: 0
            }}>
              Products
            </h1>
              
              {/* Categories Dropdown Button */}
              <Dropdown
                dropdownRender={() => buildCategoryPanel()}
                trigger={['click']}
                open={dropdownVisible}
                onOpenChange={(visible) => {
                  setDropdownVisible(visible);
                  if (visible) {
                    setHoveredTopCategory(null); // 打开时重置悬浮状态
                  }
                }}
                placement="bottomLeft"
              >
                <Button 
                  size="large"
                  style={{
                    borderColor: selectedCategory ? BRAND_PRIMARY : '#d9d9d9',
                    color: selectedCategory ? BRAND_PRIMARY : '#666',
                    fontWeight: selectedCategory ? '500' : 'normal'
                  }}
                >
                  {selectedCategory ? `Categories: ${selectedCategory}` : 'Categories'}
                  <DownOutlined style={{ marginLeft: '4px' }} />
                </Button>
              </Dropdown>

              {/* Collections Dropdown Button */}
              <Dropdown
                dropdownRender={() => buildCollectionPanel()}
                trigger={['click']}
                open={collectionDropdownVisible}
                onOpenChange={(visible) => {
                  setCollectionDropdownVisible(visible);
                  if (visible) {
                    setHoveredTopCollection(null); // 打开时重置悬浮状态
                  }
                }}
                placement="bottomLeft"
              >
                <Button 
                  size="large"
                  style={{
                    borderColor: selectedCollection ? BRAND_PRIMARY : '#d9d9d9',
                    color: selectedCollection ? BRAND_PRIMARY : '#666',
                    fontWeight: selectedCollection ? '500' : 'normal'
                  }}
                >
                  {selectedCollection ? `Collections: ${selectedCollection}` : 'Collections'}
                  <DownOutlined style={{ marginLeft: '4px' }} />
                </Button>
              </Dropdown>
            </div>
            
            <p style={{
              fontSize: '16px',
            color: '#666',
            marginBottom: 0
          }}>
            Find products to add to your store
          </p>
        </div>
        
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '8px' }}>
          {/* Search Bar */}
          <div style={{ width: '400px' }}>
            <Search
              placeholder="Search for products..."
              allowClear
              enterButton={<SearchOutlined />}
              size="large"
              onSearch={handleSearch}
              onChange={(e) => {
                if (!e.target.value) {
                  setSearchKeyword('');
                  fetchProducts(1, selectedCategoryIds, selectedCollectionIds, ''); // 清空搜索时重新加载所有产品，保持分类/商品集过滤
                }
              }}
              style={{
                width: '100%'
              }}
            />
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', color: '#666', fontSize: '14px' }}>
            <span style={{ marginRight: '4px' }}>Sort By:</span>
            <Button type="text" style={getSortButtonStyle('created_at')} onClick={handleSortByNew}>
              New In
            </Button>
            <Button type="text" style={getSortButtonStyle('purchase_count')} onClick={handleSortByPopular}>
              Popular
            </Button>
            <Button
              type="text"
              style={{ ...getSortButtonStyle('price'), display: 'flex', alignItems: 'center', paddingRight: 0 }}
              onClick={handleSortByPrice}
            >
              <span>Price</span>
              <span style={{ display: 'inline-flex', flexDirection: 'column', marginLeft: 6, lineHeight: '10px' }}>
                <UpOutlined style={{ fontSize: '10px', color: getPriceArrowColor('up') }} />
                <DownOutlined style={{ fontSize: '10px', marginTop: '-2px', color: getPriceArrowColor('down') }} />
              </span>
            </Button>
          </div>
        </div>
      </div>
    </div>

      {/* Products Grid */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: '#666' }}>Loading products...</p>
        </div>
      ) : products.length === 0 ? (
        <Empty
          description="No products found"
          style={{ padding: '60px 0' }}
        />
      ) : (
        <Row gutter={[24, 24]}>
          {products.map((product) => (
            <Col
              key={product.id}
              xs={12}
              sm={8}
              md={6}
              lg={6}
              xl={4}
            >
              <Card
                hoverable
                style={{
                  borderRadius: '12px',
                  overflow: 'hidden',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                bodyStyle={{ padding: '16px' }}
                onClick={() => window.open(`/product/${product.id}`, '_blank')}
                cover={
                  <div style={{
                    height: '200px',
                    overflow: 'hidden',
                    backgroundColor: '#f8f8f8'
                  }}>
                    <img
                      alt={product.name}
                      src={getProductImage(product)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        transition: 'transform 0.3s ease'
                      }}
                      onMouseOver={(e) => {
                        e.target.style.transform = 'scale(1.05)';
                      }}
                      onMouseOut={(e) => {
                        e.target.style.transform = 'scale(1)';
                      }}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjBmMGYwIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                      }}
                    />
                  </div>
                }
                actions={[
                  <Button
                    type="primary"
                    icon={<ShoppingCartOutlined />}
                    loading={addingProductId === product.id}
                    disabled={addingProductId === product.id}
                    onClick={(e) => {
                      e.stopPropagation(); // 阻止事件冒泡
                      handleAddToStore(product);
                    }}
                    style={{
                      backgroundColor: BRAND_PRIMARY,
                      borderColor: BRAND_PRIMARY,
                      width: '90%',
                      borderRadius: '6px'
                    }}
                  >
                    Add to My Products
                  </Button>
                ]}
              >
                <Meta
                  title={
                    <div style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: '#333',
                      marginBottom: '8px',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      lineHeight: '1.4',
                      height: '2.8em'
                    }}>
                      {product.name}
                    </div>
                  }
                  description={
                    <div>
                      <div style={{
                        fontSize: '18px',
                        fontWeight: 'bold',
                        color: BRAND_PRIMARY,
                        marginBottom: '4px'
                      }}>
                        {(() => {
                          const priceInfo = getProductPrice(product);
                          
                          // 如果是多变体产品，检查是否有多个不同的价格
                          if (product.variants && product.variants.SKU === undefined) {
                            // 收集所有变体价格
                            const allPrices = [];
                            const collectPrices = (obj) => {
                              if (obj && typeof obj === 'object') {
                                if (obj.SKU !== undefined) {
                                  allPrices.push(obj.price || 0);
                                } else {
                                  for (const value of Object.values(obj)) {
                                    collectPrices(value);
                                  }
                                }
                              }
                            };
                            collectPrices(product.variants);
                            
                            const uniquePrices = [...new Set(allPrices)];
                            if (uniquePrices.length > 1) {
                              // 有多个不同价格，显示"从 X 起"
                              return `From ${priceInfo.currency} ${priceInfo.price.toFixed(2)}`;
                            }
                          }
                          
                          // 单品或所有变体价格相同
                          return `${priceInfo.currency} ${priceInfo.price.toFixed(2)}`;
                        })()}
                      </div>
                      {product.category && (
                        <div style={{
                          fontSize: '12px',
                          color: '#999',
                          backgroundColor: '#f0f0f0',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          {product.category.name}
                        </div>
                      )}
                    </div>
                  }
                />
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* Pagination */}
      {!loading && products.length > 0 && (
        <div style={{
          display: 'flex',
          justifyContent: 'center',
          marginTop: '40px',
          paddingBottom: '20px'
        }}>
          <Pagination
            current={pagination.current}
            total={pagination.total}
            pageSize={pagination.pageSize}
            onChange={handlePageChange}
            showQuickJumper
            showSizeChanger={false}
            showTotal={(total, range) => 
              `${range[0]}-${range[1]} of ${total} items`
            }
            style={{
              backgroundColor: 'white',
              padding: '16px',
              borderRadius: '8px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
            }}
          />
        </div>
      )}
    </div>
  </>
  );
};

export default ProductsList;
